<template>
  <v-dialog v-model="dialogVisible" max-width="950px" >
    <v-card class="wage-tax-res-modal">
      <!-- 헤더 -->
      <div class="modal-header">
        <div class="header-icon">
          <v-icon size="28" color="white">mdi-file-document-check</v-icon>
        </div>
        <div class="header-content">
          <h2>임금신고 처리</h2>
          <p>{{ sisulName }}</p>
        </div>
        <button class="close-btn" @click="handleClose" :disabled="isProcessing">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <!-- 탭 -->
      <div class="tab-header">
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'pending' }"
          @click="activeTab = 'pending'"
        >
          <v-icon size="18" class="mr-1">mdi-clock-outline</v-icon>
          대기중
          <span v-if="pendingRequests.length > 0" class="tab-badge">{{ pendingRequests.length }}</span>
        </button>
        <button 
          class="tab-btn" 
          :class="{ active: activeTab === 'history' }"
          @click="activeTab = 'history'"
        >
          <v-icon size="18" class="mr-1">mdi-history</v-icon>
          처리내역
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="isLoading" class="modal-body loading-state">
        <v-progress-circular indeterminate color="primary" size="40" />
        <p>요청 목록을 불러오는 중...</p>
      </div>

      <!-- 대기중 탭 -->
      <div v-else-if="activeTab === 'pending'" class="modal-body">
        <div v-if="pendingRequests.length > 0" class="content-layout">
          <!-- 요청 리스트 (왼쪽) -->
          <div class="list-panel">
            <div class="list-header">
              <span>요청 목록</span>
              <span class="count">{{ pendingRequests.length }}건</span>
            </div>
            <div class="request-list">
              <div 
                v-for="request in pendingRequests" 
                :key="request.wtr_idx"
                class="request-row"
                :class="{ 'selected': selectedRequest?.wtr_idx === request.wtr_idx }"
                @click="selectRequest(request)"
              >
                <div class="row-main">
                  <span class="row-ym">{{ formatYearMonth(request.wtr_leb_ym) }}</span>
                  <span class="row-type">{{ request.wtr_type }}</span>
                </div>
                <div class="row-sub">
                  <span class="row-date">{{ formatDateTime(request.wtr_requested_at) }}</span>
                  <div class="row-tags">
                    <span v-if="getParsedEmployees(request.wtr_new_employees).length > 0" class="tag new">
                      입사 {{ getParsedEmployees(request.wtr_new_employees).length }}
                    </span>
                    <span v-if="getParsedEmployees(request.wtr_resigned_employees).length > 0" class="tag resigned">
                      퇴사 {{ getParsedEmployees(request.wtr_resigned_employees).length }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 상세 및 처리 패널 (오른쪽) -->
          <div class="detail-panel">
            <template v-if="selectedRequest">
              <!-- 상세 정보 -->
              <div class="detail-section">
                <h4>요청 상세</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="label">귀속연월</span>
                    <span class="value">{{ formatYearMonth(selectedRequest.wtr_leb_ym) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">요청유형</span>
                    <span class="value">{{ selectedRequest.wtr_type }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">요청일시</span>
                    <span class="value">{{ formatDateTime(selectedRequest.wtr_requested_at) }}</span>
                  </div>
                </div>

                <div v-if="getParsedEmployees(selectedRequest.wtr_new_employees).length > 0" class="detail-item full">
                  <span class="label">입사자</span>
                  <div class="emp-chips">
                    <span v-for="emp in getParsedEmployees(selectedRequest.wtr_new_employees)" :key="emp.emp_code" class="emp-chip new">
                      {{ emp.emp_name }}
                    </span>
                  </div>
                </div>

                <div v-if="getParsedEmployees(selectedRequest.wtr_resigned_employees).length > 0" class="detail-item full">
                  <span class="label">퇴사자</span>
                  <div class="emp-chips">
                    <span v-for="emp in getParsedEmployees(selectedRequest.wtr_resigned_employees)" :key="emp.emp_code" class="emp-chip resigned">
                      {{ emp.emp_name }}
                    </span>
                  </div>
                </div>

                <div v-if="selectedRequest.wtr_request_memo" class="detail-item full">
                  <span class="label">요청메모</span>
                  <div class="memo-box">{{ selectedRequest.wtr_request_memo }}</div>
                </div>
              </div>

              <!-- 처리 영역 -->
              <div class="process-section">
                <h4>처리</h4>
                <div class="process-notice">
                  <v-icon size="16" color="#f59e0b">mdi-information</v-icon>
                  <span>실제 국세청 임금신고는 크롤링 기능 추가 후 자동 처리됩니다.</span>
                </div>
                <v-textarea
                  v-model="resultMemo"
                  variant="outlined"
                  placeholder="결과 메모 (미승인 시 사유 필수)"
                  rows="2"
                  hide-details
                  density="compact"
                  class="result-textarea"
                />
                <div class="process-actions">
                  <button class="btn-reject" @click="handleProcess('미승인')" :disabled="isProcessing">
                    <v-progress-circular v-if="isProcessing && processAction === '미승인'" indeterminate size="14" width="2" color="white" class="mr-1" />
                    미승인
                  </button>
                  <button class="btn-approve" @click="handleProcess('승인')" :disabled="isProcessing">
                    <v-progress-circular v-if="isProcessing && processAction === '승인'" indeterminate size="14" width="2" color="white" class="mr-1" />
                    승인
                  </button>
                </div>
              </div>
            </template>
            <div v-else class="no-selection">
              <v-icon size="40" color="#d1d5db">mdi-gesture-tap</v-icon>
              <p>요청을 선택하세요</p>
            </div>
          </div>
        </div>

        <!-- 대기중 요청 없음 -->
        <div v-else class="empty-state">
          <v-icon size="56" color="#d1d5db">mdi-check-circle-outline</v-icon>
          <h3>대기중인 요청이 없습니다</h3>
          <p>모든 임금신고 요청이 처리되었습니다</p>
        </div>
      </div>

      <!-- 처리내역 탭 -->
      <div v-else-if="activeTab === 'history'" class="modal-body">
        <div v-if="historyRequests.length > 0" class="content-layout">
          <!-- 처리내역 리스트 (왼쪽) -->
          <div class="list-panel">
            <div class="list-header">
              <span>처리 내역</span>
              <span class="count">{{ historyRequests.length }}건</span>
            </div>
            <div class="request-list">
              <div 
                v-for="request in historyRequests" 
                :key="request.wtr_idx"
                class="request-row"
                :class="{ 'selected': selectedHistoryRequest?.wtr_idx === request.wtr_idx }"
                @click="selectHistoryRequest(request)"
              >
                <div class="row-main">
                  <span class="row-ym">{{ formatYearMonth(request.wtr_leb_ym) }}</span>
                  <span class="status-badge-small" :class="getStatusClass(request.wtr_status)">
                    {{ request.wtr_status }}
                  </span>
                </div>
                <div class="row-sub">
                  <span class="row-date">{{ formatDateTime(request.wtr_requested_at) }}</span>
                  <div class="row-tags">
                    <span v-if="getParsedEmployees(request.wtr_new_employees).length > 0" class="tag new">
                      입사 {{ getParsedEmployees(request.wtr_new_employees).length }}
                    </span>
                    <span v-if="getParsedEmployees(request.wtr_resigned_employees).length > 0" class="tag resigned">
                      퇴사 {{ getParsedEmployees(request.wtr_resigned_employees).length }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 상세 정보 패널 (오른쪽) -->
          <div class="detail-panel">
            <template v-if="selectedHistoryRequest">
              <!-- 요청 상세 정보 -->
              <div class="detail-section">
                <h4>요청 상세</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="label">귀속연월</span>
                    <span class="value">{{ formatYearMonth(selectedHistoryRequest.wtr_leb_ym) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">요청유형</span>
                    <span class="value">{{ selectedHistoryRequest.wtr_type }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">요청일시</span>
                    <span class="value">{{ formatDateTime(selectedHistoryRequest.wtr_requested_at) }}</span>
                  </div>
                </div>

                <div v-if="getParsedEmployees(selectedHistoryRequest.wtr_new_employees).length > 0" class="detail-item full">
                  <span class="label">입사자</span>
                  <div class="emp-chips">
                    <span v-for="emp in getParsedEmployees(selectedHistoryRequest.wtr_new_employees)" :key="emp.emp_code" class="emp-chip new">
                      {{ emp.emp_name }}
                    </span>
                  </div>
                </div>

                <div v-if="getParsedEmployees(selectedHistoryRequest.wtr_resigned_employees).length > 0" class="detail-item full">
                  <span class="label">퇴사자</span>
                  <div class="emp-chips">
                    <span v-for="emp in getParsedEmployees(selectedHistoryRequest.wtr_resigned_employees)" :key="emp.emp_code" class="emp-chip resigned">
                      {{ emp.emp_name }}
                    </span>
                  </div>
                </div>

                <div v-if="selectedHistoryRequest.wtr_request_memo" class="detail-item full">
                  <span class="label">요청메모</span>
                  <div class="memo-box">{{ selectedHistoryRequest.wtr_request_memo }}</div>
                </div>
              </div>

              <!-- 처리 결과 -->
              <div class="result-section" :class="getStatusClass(selectedHistoryRequest.wtr_status)">
                <div class="result-header">
                  <v-icon size="20" :color="getStatusIconColor(selectedHistoryRequest.wtr_status)">
                    {{ getStatusIcon(selectedHistoryRequest.wtr_status) }}
                  </v-icon>
                  <h4>처리 결과: {{ selectedHistoryRequest.wtr_status }}</h4>
                </div>
                <div class="result-grid">
                  <div class="result-item">
                    <span class="label">처리일시</span>
                    <span class="value">{{ formatDateTime(selectedHistoryRequest.wtr_processed_at || selectedHistoryRequest.wtr_cancelled_at) }}</span>
                  </div>
                  <div class="result-item">
                    <span class="label">처리자</span>
                    <span class="value">{{ selectedHistoryRequest.wtr_processed_by || selectedHistoryRequest.wtr_cancelled_by || '-' }}</span>
                  </div>
                </div>
                <div v-if="selectedHistoryRequest.wtr_result_memo || selectedHistoryRequest.wtr_cancel_reason" class="result-item full">
                  <span class="label">{{ selectedHistoryRequest.wtr_status === '취소' ? '취소사유' : '결과메모' }}</span>
                  <div class="memo-box" :class="getStatusClass(selectedHistoryRequest.wtr_status)">
                    {{ selectedHistoryRequest.wtr_result_memo || selectedHistoryRequest.wtr_cancel_reason }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="no-selection">
              <v-icon size="40" color="#d1d5db">mdi-gesture-tap</v-icon>
              <p>처리내역을 선택하세요</p>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <v-icon size="56" color="#d1d5db">mdi-history</v-icon>
          <h3>처리 내역이 없습니다</h3>
        </div>
      </div>

      <!-- 푸터 -->
      <div class="modal-footer">
        <button class="btn-close" @click="handleClose" :disabled="isProcessing">
          닫기
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

interface WageTaxRequest {
  wtr_idx: number;
  m_sisul_code: string;
  wtr_leb_ym: string;
  wtr_type: string;
  wtr_status: string;
  wtr_new_employees?: string;
  wtr_resigned_employees?: string;
  wtr_request_memo?: string;
  wtr_requested_by?: string;
  wtr_requested_at?: string;
  wtr_cancelled_by?: string;
  wtr_cancelled_at?: string;
  wtr_cancel_reason?: string;
  wtr_result_memo?: string;
  wtr_processed_by?: string;
  wtr_processed_at?: string;
  m_sisul_name?: string;
}

const props = defineProps<{
  modelValue: boolean;
  sisulCode: string;
  sisulName: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'processed'): void;
}>();

const store = useStore();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// 상태
const isLoading = ref(false);
const isProcessing = ref(false);
const processAction = ref('');
const activeTab = ref<'pending' | 'history'>('pending');
const pendingRequests = ref<WageTaxRequest[]>([]);
const historyRequests = ref<WageTaxRequest[]>([]);
const selectedRequest = ref<WageTaxRequest | null>(null);
const selectedHistoryRequest = ref<WageTaxRequest | null>(null);
const resultMemo = ref('');

// JSON 파싱 헬퍼
const getParsedEmployees = (json?: string) => {
  if (!json) return [];
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
};

// 상태 클래스
const getStatusClass = (status: string) => {
  switch (status) {
    case '승인': return 'approved';
    case '미승인': return 'rejected';
    case '취소': return 'cancelled';
    default: return '';
  }
};

// 날짜 포맷
const formatDateTime = (datetime?: string) => {
  if (!datetime) return '-';
  const d = new Date(datetime);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatYearMonth = (ym?: string) => {
  if (!ym || ym.length !== 6) return ym || '';
  return `${ym.substring(0, 4)}.${ym.substring(4, 6)}`;
};

// 요청 선택 (대기중)
const selectRequest = (request: WageTaxRequest) => {
  selectedRequest.value = request;
  resultMemo.value = '';
};

// 처리내역 선택
const selectHistoryRequest = (request: WageTaxRequest) => {
  selectedHistoryRequest.value = request;
};

// 상태 아이콘
const getStatusIcon = (status: string) => {
  switch (status) {
    case '승인': return 'mdi-check-circle';
    case '미승인': return 'mdi-close-circle';
    case '취소': return 'mdi-cancel';
    default: return 'mdi-help-circle';
  }
};

// 상태 아이콘 색상
const getStatusIconColor = (status: string) => {
  switch (status) {
    case '승인': return '#10b981';
    case '미승인': return '#ef4444';
    case '취소': return '#6b7280';
    default: return '#9ca3af';
  }
};

// 데이터 로드
const loadRequests = async () => {
  if (!props.sisulCode) return;

  isLoading.value = true;
  try {
    // 대기중 요청
    const pendingRes = await axios.get('/api/wage-process/tax-request/list', {
      params: { sisulCode: props.sisulCode, status: '요청' }
    });
    if (pendingRes.data.success) {
      pendingRequests.value = pendingRes.data.data || [];
    }

    // 처리 내역 (승인, 미승인, 취소)
    const historyRes = await axios.get('/api/wage-process/tax-request/list', {
      params: { sisulCode: props.sisulCode }
    });
    if (historyRes.data.success) {
      historyRequests.value = (historyRes.data.data || []).filter(
        (r: WageTaxRequest) => r.wtr_status !== '요청'
      );
    }

    selectedRequest.value = null;
    selectedHistoryRequest.value = null;
  } catch (error) {
    console.error('요청 목록 로드 오류:', error);
  } finally {
    isLoading.value = false;
  }
};

// 요청 처리
const handleProcess = async (status: '승인' | '미승인') => {
  if (!selectedRequest.value) return;

  if (status === '미승인' && !resultMemo.value.trim()) {
    store.commit('showSnackbar', {
      text: '미승인 사유를 입력해주세요.',
      color: 'warning'
    });
    return;
  }

  const user = store.state.user;
  isProcessing.value = true;
  processAction.value = status;

  try {
    const response = await axios.post('/api/wage-process/tax-request/process', {
      wtrIdx: selectedRequest.value.wtr_idx,
      status,
      resultMemo: resultMemo.value,
      processedBy: user?.m_id || 'admin'
    });

    if (response.data.success) {
      store.commit('showSnackbar', {
        text: `요청이 ${status}되었습니다.`,
        color: 'success'
      });
      emit('processed');
      await loadRequests();
    } else {
      throw new Error(response.data.message || '처리 실패');
    }
  } catch (error: any) {
    console.error('요청 처리 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '처리 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isProcessing.value = false;
    processAction.value = '';
  }
};

// 닫기
const handleClose = () => {
  dialogVisible.value = false;
  selectedRequest.value = null;
  selectedHistoryRequest.value = null;
  resultMemo.value = '';
  activeTab.value = 'pending';
};

// 다이얼로그 열릴 때 데이터 로드
watch(() => props.modelValue, (newVal) => {
  if (newVal && props.sisulCode) {
    loadRequests();
  }
});

watch(() => props.sisulCode, () => {
  if (props.modelValue && props.sisulCode) {
    loadRequests();
  }
});
</script>

<style lang="scss" scoped>
.wage-tax-res-modal {
  border-radius: 16px !important;
  overflow: hidden;

  .modal-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px 24px;
    background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);

    .header-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .header-content {
      flex: 1;

      h2 {
        font-size: 1.125rem;
        font-weight: 700;
        color: white;
        margin: 0 0 2px 0;
      }

      p {
        font-size: 0.813rem;
        color: rgba(255, 255, 255, 0.8);
        margin: 0;
      }
    }

    .close-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.3);
      }
    }
  }

  .tab-header {
    display: flex;
    gap: 4px;
    padding: 12px 24px;
    background: #f8fafc;
    border-bottom: 1px solid #e5e7eb;

    .tab-btn {
      display: flex;
      align-items: center;
      padding: 8px 16px;
      border: none;
      background: transparent;
      border-radius: 8px;
      font-size: 0.813rem;
      font-weight: 600;
      color: #6b7280;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #e5e7eb;
      }

      &.active {
        background: #1e3a8a;
        color: white;
      }

      .tab-badge {
        margin-left: 6px;
        padding: 2px 6px;
        background: #ef4444;
        color: white;
        border-radius: 10px;
        font-size: 0.688rem;
        font-weight: 700;
      }
    }
  }

  .modal-body {
    padding: 20px 24px;
    min-height: 400px;
    max-height: 55vh;
    overflow-y: auto;

    &.loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;

      p {
        color: #6b7280;
        font-size: 0.875rem;
      }
    }

    .content-layout {
      display: flex;
      gap: 20px;
      height: 100%;
    }

    .list-panel {
      width: 320px;
      flex-shrink: 0;
      display: flex;
      flex-direction: column;

      .list-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: #f1f5f9;
        border-radius: 8px 8px 0 0;
        font-size: 0.75rem;
        font-weight: 600;
        color: #475569;

        .count {
          color: #1e3a8a;
        }
      }

      .request-list {
        border: 1px solid #e5e7eb;
        border-top: none;
        border-radius: 0 0 8px 8px;
        overflow-y: auto;
        max-height: 340px;

        .request-row {
          padding: 10px 12px;
          border-bottom: 1px solid #f1f5f9;
          cursor: pointer;
          transition: all 0.15s ease;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background: #f9fafb;
          }

          &.selected {
            background: #eff6ff;
            border-left: 3px solid #3b82f6;
          }

          .row-main {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;

            .row-ym {
              font-size: 0.875rem;
              font-weight: 700;
              color: #1e3a8a;
            }

            .row-type {
              font-size: 0.75rem;
              color: #6b7280;
              background: #f1f5f9;
              padding: 2px 6px;
              border-radius: 4px;
            }

            .status-badge-small {
              font-size: 0.625rem;
              font-weight: 700;
              padding: 2px 6px;
              border-radius: 4px;

              &.approved {
                background: #d1fae5;
                color: #065f46;
              }

              &.rejected {
                background: #fee2e2;
                color: #991b1b;
              }

              &.cancelled {
                background: #f3f4f6;
                color: #6b7280;
              }
            }
          }

          .row-sub {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .row-date {
              font-size: 0.688rem;
              color: #9ca3af;
            }

            .row-tags {
              display: flex;
              gap: 4px;

              .tag {
                font-size: 0.625rem;
                font-weight: 600;
                padding: 1px 5px;
                border-radius: 4px;

                &.new {
                  background: #dbeafe;
                  color: #1e40af;
                }

                &.resigned {
                  background: #fee2e2;
                  color: #991b1b;
                }
              }
            }
          }
        }
      }
    }

    .detail-panel {
      flex: 1;
      min-width: 0;

      .detail-section {
        background: #f9fafb;
        border-radius: 10px;
        padding: 16px;
        margin-bottom: 16px;

        h4 {
          font-size: 0.813rem;
          font-weight: 700;
          color: #374151;
          margin: 0 0 12px 0;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 12px;
        }

        .detail-item {
          &.full {
            margin-top: 12px;
          }

          .label {
            display: block;
            font-size: 0.688rem;
            font-weight: 600;
            color: #9ca3af;
            margin-bottom: 4px;
          }

          .value {
            font-size: 0.813rem;
            font-weight: 600;
            color: #1f2937;
          }

          .emp-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;

            .emp-chip {
              padding: 3px 8px;
              border-radius: 10px;
              font-size: 0.75rem;
              font-weight: 600;

              &.new {
                background: #dbeafe;
                color: #1e40af;
              }

              &.resigned {
                background: #fee2e2;
                color: #991b1b;
              }
            }
          }

          .memo-box {
            background: white;
            padding: 10px 12px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            font-size: 0.813rem;
            color: #374151;
            line-height: 1.5;
            white-space: pre-wrap;
          }
        }
      }

      .process-section {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 16px;

        h4 {
          font-size: 0.813rem;
          font-weight: 700;
          color: #374151;
          margin: 0 0 10px 0;
        }

        .process-notice {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: #fffbeb;
          border-radius: 6px;
          margin-bottom: 12px;

          span {
            font-size: 0.688rem;
            color: #92400e;
          }
        }

        .result-textarea {
          margin-bottom: 12px;

          :deep(.v-field) {
            border-radius: 6px;
            background: white;
            font-size: 0.813rem;
          }
        }

        .process-actions {
          display: flex;
          gap: 10px;

          .btn-reject,
          .btn-approve {
            flex: 1;
            height: 38px;
            border: none;
            border-radius: 8px;
            font-size: 0.813rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .btn-reject {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;

            &:hover:not(:disabled) {
              background: #fee2e2;
            }
          }

          .btn-approve {
            background: #10b981;
            color: white;

            &:hover:not(:disabled) {
              background: #059669;
            }
          }
        }
      }

      .result-section {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 16px;

        &.approved {
          background: #ecfdf5;
          border-color: #a7f3d0;
        }

        &.rejected {
          background: #fef2f2;
          border-color: #fecaca;
        }

        &.cancelled {
          background: #f9fafb;
          border-color: #e5e7eb;
        }

        .result-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;

          h4 {
            font-size: 0.875rem;
            font-weight: 700;
            color: #374151;
            margin: 0;
          }
        }

        .result-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 12px;
        }

        .result-item {
          &.full {
            margin-top: 8px;
          }

          .label {
            display: block;
            font-size: 0.688rem;
            font-weight: 600;
            color: #9ca3af;
            margin-bottom: 4px;
          }

          .value {
            font-size: 0.813rem;
            font-weight: 600;
            color: #1f2937;
          }

          .memo-box {
            background: white;
            padding: 10px 12px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            font-size: 0.813rem;
            color: #374151;
            line-height: 1.5;
            white-space: pre-wrap;

            &.approved {
              border-color: #a7f3d0;
              background: #f0fdf4;
            }

            &.rejected {
              border-color: #fecaca;
              background: #fff5f5;
            }
          }
        }
      }

      .no-selection {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 300px;
        gap: 10px;
        color: #9ca3af;

        p {
          font-size: 0.875rem;
          margin: 0;
        }
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 300px;
      text-align: center;

      h3 {
        font-size: 1rem;
        font-weight: 700;
        color: #374151;
        margin: 0;
      }

      p {
        font-size: 0.813rem;
        color: #9ca3af;
        margin: 0;
      }
    }

    .history-table-wrapper {
      overflow-x: auto;

      .history-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.813rem;

        th, td {
          padding: 10px 12px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }

        th {
          background: #f8fafc;
          font-weight: 700;
          color: #374151;
          white-space: nowrap;
        }

        td {
          color: #4b5563;
        }

        .status-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 10px;
          font-size: 0.688rem;
          font-weight: 700;

          &.approved {
            background: #d1fae5;
            color: #065f46;
          }

          &.rejected {
            background: #fee2e2;
            color: #991b1b;
          }

          &.cancelled {
            background: #f3f4f6;
            color: #6b7280;
          }
        }

        .no-memo {
          color: #d1d5db;
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 14px 24px;
    border-top: 1px solid #f1f3f5;
    background: #fafbfc;

    .btn-close {
      height: 38px;
      padding: 0 20px;
      background: white;
      color: #4b5563;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      font-size: 0.813rem;
      font-weight: 600;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #f9fafb;
      }
    }
  }
}

.mr-1 {
  margin-right: 4px;
}
</style>
